/**
 * PlayerSourceSelector View Component
 *
 * Segmented control for switching between Watchlist and Roster player sources.
 * Provides a clear, mutually exclusive choice between the two data sources.
 */

import React, { memo } from "react";

import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";
import { MY_ROSTER_KANBAN } from "@/config/featureFlags";

/**
 * Player source type - either watchlist or roster
 */
export type PlayerSource = "watchlist" | "roster";

/**
 * Props for PlayerSourceSelector view component
 */
interface PlayerSourceSelectorViewProps {
  selectedSource: PlayerSource;
  onSelectWatchlist: () => void;
  onSelectRoster: () => void;
}

/**
 * View component for rendering a segmented control to switch between watchlist and roster player sources.
 * @param root0 - The component props
 * @param root0.selectedSource - The currently selected player source (watchlist or roster)
 * @param root0.onSelectWatchlist - Callback invoked when the user clicks the watchlist button
 * @param root0.onSelectRoster - Callback invoked when the user clicks the roster button
 */
const PlayerSourceSelectorView = ({
  selectedSource,
  onSelectWatchlist,
  onSelectRoster,
}: PlayerSourceSelectorViewProps) =>
  MY_ROSTER_KANBAN !== "on" ? null : (
    <Stack
      direction="horizontal"
      className="rounded-sm border border-outline-subtle bg-surface-raised p-1"
    >
      {/* Watchlist Button */}
      <Pressable
        onPress={onSelectWatchlist}
        className={`flex-1 items-center justify-center rounded-none py-2 ${
          selectedSource === "watchlist"
            ? "bg-accent-primary"
            : "bg-transparent"
        }`}
      >
        <Text
          variant="body-strong"
          className={
            selectedSource === "watchlist"
              ? "text-content-on-accent"
              : "text-content-muted"
          }
        >
          Watchlist
        </Text>
      </Pressable>

      {/* My Roster Button */}
      <Pressable
        onPress={onSelectRoster}
        className={`flex-1 items-center justify-center rounded-none py-2 ${
          selectedSource === "roster" ? "bg-accent-primary" : "bg-transparent"
        }`}
      >
        <Text
          variant="body-strong"
          className={
            selectedSource === "roster"
              ? "text-content-on-accent"
              : "text-content-muted"
          }
        >
          My Roster
        </Text>
      </Pressable>
    </Stack>
  );

PlayerSourceSelectorView.displayName = "PlayerSourceSelectorView";

export default memo(PlayerSourceSelectorView);
